import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { TransactionResult } from 'src/common/contracts/hooks/useExecuteTx'
import useCarbon from 'src/common/queries/useCarbon'
import { AnyToken } from 'src/common/types/models/Tokens'
import NumericField from 'src/common/ui-kit/NumericField/NumericField'
import TokenIcon from 'src/common/ui-kit/TokenIcon/TokenIcon'
import TransactionButton from '../../Buttons/TransactionButton'
import { viccCountAtom, withdrawnNFTsAtom } from '../bundle-store'
import MaxMinInput from '../components/MaxMinInput'
import useCarbonBundleApproval from './hooks/useCarbonBundleApproval'
import useCarbonBundleWithdrawState from './hooks/useCarbonBundleWithdrawState'
import useCarbonBundleWithdrawTx from './hooks/useCarbonBundleWithdrawTx'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import { useAllTokensForOwner } from 'src/common/queries/useTokensForOwner/useAllTokensForOwner'
import useLockedBalance from 'src/pages/WalletPage/hooks/useLockedBalance'
import useCarbonBundleInfo from 'src/common/contracts/hooks/carbon-bundles/useCarbonBundleInfo'

interface CarbonBundleDepositProps {
  onSuccess(tx: TransactionResult): void
}

const CarbonBundleWithdraw: React.FC<CarbonBundleDepositProps> = ({
  onSuccess,
}) => {
  const { carbonBundleInfo, loading: bundleInfoLoading } = useCarbonBundleInfo()
  const { t } = useTranslation('carbonBundles', {
    keyPrefix: 'withdrawModal',
  })

  const { loading: approvalLoading, approveCarbon } = useCarbonBundleApproval()

  const [viccCount, setViccCount] = useAtom(viccCountAtom)
  const updateViccCount = (v: number) => {
    v = Math.floor(v)
    setViccCount(v)
  }
  const setWithdrawnNFTs = useSetAtom(withdrawnNFTsAtom)

  const { carbon } = useCarbon()
  const {
    maxValue,
    burnValue,
    loading: stateLoading,
    vicc,
  } = useCarbonBundleWithdrawState()

  const { withdrawAssetsTx, withdrawnCarbonNfts } = useCarbonBundleWithdrawTx()

  const { smartAccountInfo } = useSmartAccount()
  const { refetch: lockedRefetch } = useLockedBalance()
  const { refetch: allTokensRefetch } = useAllTokensForOwner(
    smartAccountInfo?.address || ''
  )
  const handleSuccess = (tx: TransactionResult) => {
    setWithdrawnNFTs(withdrawnCarbonNfts)
    onSuccess?.(tx)
    lockedRefetch()
    allTokensRefetch()
  }

  const tx = approveCarbon
    ? [approveCarbon!, withdrawAssetsTx!]
    : [withdrawAssetsTx!]

  const txButtonDisabled = !viccCount
  const txButtonLoading = approvalLoading || stateLoading

  const txButtonCaption = t('actions.confirm')

  return (
    <div className='flex flex-col'>
      <h2 className='modal-title'>{t('header')}</h2>

      <p className='modal-description'>{t('info.burn')}</p>

      <ul className='marked-list mb-2'>
        <li>
          {t('info.carbonPrice', {
            price: 1,
          })}
        </li>
      </ul>

      <h3 className='modal-subtitle'>{t('withdraw')}</h3>

      <div className='flex flex-col gap-2 justify-between mb-2'>
        <MaxMinInput
          onChange={updateViccCount}
          value={viccCount}
          maxValue={maxValue}
          token={vicc as AnyToken}
        />
      </div>

      <h3 className='modal-subtitle'>{t('burn')}</h3>
      <NumericField
        className=''
        value={burnValue}
        StartAdornment={carbon && <TokenIcon token={carbon} />}
        EndAdornment={
          <span className='modal-description'>{carbon?.symbol}</span>
        }
        readonly
      />
      {!bundleInfoLoading && (
        <div className='mb-2'>
          <p className='modal-description'>
            {t('info.withdrawalFee', {
              fee: carbonBundleInfo.withdrawalFee?.toString(),
            })}
          </p>
        </div>
      )}
      <TransactionButton
        className='w-full px-2'
        loading={txButtonLoading}
        disabled={txButtonDisabled}
        transaction={tx}
        onSuccess={handleSuccess}
      >
        {txButtonCaption}
      </TransactionButton>
    </div>
  )
}

export default CarbonBundleWithdraw
